import { getNetworkAlertFields } from "../../AnalysisReports/Content/Tabs/SnortSuricata/utils/snortSuricataUtil";
import { MENU_ITEMS as IOC_ITEMS } from "../../../Components/PagesComponents/AnalysisReports/MemoryBlock/Tabs/IOCs/constants/constants";
import { formatMatchedApi } from "../../AnalysisReports/AndroidAnalysisReports/Content/Tabs/Crimes/crimesUtils";

// The Android page has no verdict field: the verdict travels in the ?state=
// query param, and isApt overrides it, exactly as the page header renders it.
export const getAndroidVerdict = (general, stateParam) =>
  general?.isApt ? "malicious" : stateParam || "unknown";

export const getAndroidReportTime = (general) => general?.report_date || "None";

// Several slices initialise to {} rather than [], and reset to {} on failure,
// so every list has to be guarded before it is mapped.
const asArray = (value) => (Array.isArray(value) ? value : []);

const asText = (value) =>
  value === 0 || value === false
    ? String(value)
    : value === null || value === undefined
    ? ""
    : value;

const PLUGIN_GROUPS = [
  { key: "Library", label: "Library" },
  { key: "DeletedFiles", label: "Deleted Files" },
  { key: "File", label: "Files" },
  { key: "SharedPreferences", label: "Shared Preferences" },
];

// Unlike the ThreatHunter report there is no single aggregate object on the
// Android side: the data lives in ten slices, so everything arrives as one
// options object.
export const getAndroidReportFields = ({
  general,
  apkInfo,
  filesInfo,
  crimes,
  staticCodeIssues,
  permissions,
  plugins,
  iocs,
  networkActivityMap,
  snortSuricataAlerts,
  stateParam,
} = {}) => {
  // general.sha1 actually holds the sha256; the real md5/sha1/ssdeep come from
  // filesInfo, the same way the sandbox report export reads them.
  const targetFile = asArray(filesInfo)[0]?.target?.file || {};

  return {
    general: {
      target: general?.file_name || null,
      size: general?.size ?? null,
      md5: targetFile.md5 || null,
      sha1: targetFile.sha1 || null,
      sha256: general?.sha1 || null,
      ssdeep: targetFile.ssdeep || null,
      analysisStarted: general?.analysis_started || null,
      reportDate: general?.report_date || null,
      decisionScore: general?.decision_score ?? null,
      verdict: getAndroidVerdict(general, stateParam),
      isApt: Boolean(general?.isApt),
      engines: {
        virustotal: general?.vt_status ?? null,
        clamav: general?.av_status ?? null,
        cloud: general?.cloud_status ?? null,
        mlChecker: general?.mlchecker_status ?? null,
        yara: general?.yara_status ?? null,
        networkTraffic: general?.networktraffic_status ?? null,
      },
    },
    apk: {
      name: apkInfo?.name || null,
      package: apkInfo?.package || null,
      versionCode: apkInfo?.version_code ?? null,
      versionName: apkInfo?.version_name || null,
      targetSdk: apkInfo?.target_sdk ?? null,
      minSdk: apkInfo?.min_sdk ?? null,
      isSigned: apkInfo?.is_signed ?? null,
      validApk: apkInfo?.valid_apk ?? null,
    },
    possibleMaliciousActions: asArray(crimes).map((crime) => ({
      crime: crime?.crime || null,
      score: crime?.score ?? null,
      confidence: crime?.confidence ?? null,
      weight: crime?.weight ?? null,
      combination: asArray(crime?.combination),
      matchedApi: asArray(crime?.matched_api),
      permissions: asArray(crime?.permissions),
      register: asArray(crime?.register),
      sequence: asArray(crime?.sequence),
    })),
    staticCodeIssues: asArray(staticCodeIssues).map((issue) => ({
      description: issue?.metadata?.description || null,
      cwe: issue?.metadata?.cwe || null,
      masvs: issue?.metadata?.masvs || null,
      owaspMobile: issue?.metadata?.["owasp-mobile"] || null,
      severity: issue?.metadata?.severity || null,
    })),
    permissions: asArray(permissions).map((permission) => ({
      name: permission?.simplified_name || null,
      severity: permission?.severity || null,
      tags: asArray(permission?.tags),
      description: permission?.description || null,
      manifestPermission: permission?.permission || null,
      details: permission?.details || null,
    })),
    plugins: PLUGIN_GROUPS.reduce(
      (acc, group) => ({ ...acc, [group.key]: asArray(plugins?.[group.key]) }),
      {},
    ),
    network: {
      activity: asArray(networkActivityMap),
      alerts: getNetworkAlertFields(snortSuricataAlerts),
    },
    iocs: IOC_ITEMS.reduce((acc, item) => ({ ...acc, [item.id]: asArray(iocs?.[item.id]) }), {}),
  };
};

// Descriptions and manifest permission names routinely contain commas, so every
// field is quoted and embedded quotes are doubled per RFC 4180.
const toCsvCell = (value) => '"' + String(value ?? "").replace(/"/g, '""') + '"';

const toCsvRows = (rows) => rows.map((row) => row.map(toCsvCell).join(",")).join("\n");

export const getAndroidCsvData = (options) => {
  const report = getAndroidReportFields(options);

  const blocks = [
    toCsvRows([
      [
        "File Name",
        "Size",
        "MD5",
        "SHA1",
        "SHA256",
        "SSDEEP",
        "Verdict",
        "Decision Score",
        "Analysis Started",
        "Report Date",
      ],
      [
        report.general.target,
        report.general.size,
        report.general.md5,
        report.general.sha1,
        report.general.sha256,
        report.general.ssdeep,
        report.general.verdict,
        report.general.decisionScore,
        report.general.analysisStarted,
        report.general.reportDate,
      ],
    ]),
    toCsvRows([
      ["APK Information"],
      ["Field", "Value"],
      ["Application Name", report.apk.name],
      ["Package Name", report.apk.package],
      ["Version Code", report.apk.versionCode],
      ["Version Name", report.apk.versionName],
      ["Target Sdk Version", report.apk.targetSdk],
      ["Min Sdk Version", report.apk.minSdk],
      ["Signed", asText(report.apk.isSigned)],
      ["Valid", asText(report.apk.validApk)],
    ]),
  ];

  // The summary alone loses the findings, so each list follows as its own
  // labelled block in the same file. Nothing here is capped: the pdf truncates
  // long lists and points the reader at this export.
  if (report.possibleMaliciousActions.length) {
    blocks.push(
      toCsvRows([
        ["Possible Malicious Actions"],
        ["Description", "Score", "Confidence", "Weight", "Matched API"],
        ...report.possibleMaliciousActions.map((action) => [
          action.crime,
          action.score,
          action.confidence,
          action.weight,
          asArray(action.matchedApi).map(formatMatchedApi).join("; "),
        ]),
      ]),
    );
  }

  if (report.staticCodeIssues.length) {
    blocks.push(
      toCsvRows([
        ["Static Code Issues"],
        ["Description", "CWE", "MASVS", "Owasp Mobile", "Severity"],
        ...report.staticCodeIssues.map((issue) => [
          issue.description,
          issue.cwe,
          issue.masvs,
          issue.owaspMobile,
          issue.severity,
        ]),
      ]),
    );
  }

  if (report.permissions.length) {
    blocks.push(
      toCsvRows([
        ["Permissions"],
        ["Name", "Severity", "Tags", "Description", "Manifest Permission", "Details"],
        ...report.permissions.map((permission) => [
          permission.name,
          permission.severity,
          permission.tags.join("; "),
          permission.description,
          permission.manifestPermission,
          permission.details,
        ]),
      ]),
    );
  }

  const pluginRows = PLUGIN_GROUPS.flatMap((group) =>
    report.plugins[group.key].map((row) => [
      group.label,
      row?.date,
      row?.method,
      row?.name || row?.file_path,
      row?.value,
    ]),
  );

  if (pluginRows.length) {
    blocks.push(
      toCsvRows([["Plugins"], ["Group", "Date", "Method", "Name/File Path", "Value"], ...pluginRows]),
    );
  }

  if (report.network.activity.length) {
    blocks.push(
      toCsvRows([
        ["Network Activity"],
        ["IP", "Port", "Protocol", "Country", "Hostname/SNI"],
        ...report.network.activity.map((activity) => [
          activity?.ip,
          activity?.port,
          activity?.protocol,
          activity?.country_name || activity?.country_code,
          activity?.sni || activity?.hostname,
        ]),
      ]),
    );
  }

  if (report.network.alerts.length) {
    blocks.push(
      toCsvRows([
        ["Network Alerts"],
        ["Source IP", "Destination IP", "Signature", "Protocol", "Country"],
        ...report.network.alerts.map((alert) => [
          alert.srcIp,
          alert.destIp,
          alert.signature,
          alert.protocol,
          alert.country_code,
        ]),
      ]),
    );
  }

  const iocRows = IOC_ITEMS.flatMap((item) =>
    report.iocs[item.id].map((value) => [item.header, value]),
  );

  if (iocRows.length) {
    blocks.push(toCsvRows([["IOCs"], ["Type", "Value"], ...iocRows]));
  }

  return blocks.join("\n\n");
};
